import React, { useState, useMemo, useEffect } from 'react';
import Modal from 'ds/components/modal';
import { Button } from 'ds/components/button';
import { useLocale } from 'locale';
import { Text } from 'ds/components/typography';
import * as Styled from './styled';
import { optionsByDealType, ReasonType, REASON_MAX_LENGTH } from './helpers';
import RadioGroup from 'ds/components/input/RadioGroup';
import { Textarea } from 'ds/components/input/Textarea';
import defaultTheme from 'ds/theme/default';
import { trim } from 'lodash';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { UpdateListingStatusPopupProps } from './types';
import withEvents from 'analytics/withEvents';
import { PocType, BulletinStatusType } from 'utils/entities';
import { poiEventPayload } from 'store/sagas/analytics/property';
import { PromotionPlanVariant } from 'store/sagas/apiService/types';
import PromotionPlanInfoSection from 'components/private-bulletin-promotion/PromotionPlanInfoSection';

type StepType = 'REASONS' | 'PROMOTION_PLAN';

const UpdateListingStatusPopup: React.FC<UpdateListingStatusPopupProps> = (props) => {
  const {
    poi,
    status,
    source,
    onPopupOpen,
    editListingStatus,
    setModalOpenStatus,
    promotionPlan,
    onCancelPromotionPlan,
    cancelSubscriptionThenEditListingStatus,
  } = props;

  const [ reason, setReason ] = useState(null);
  const [ reasonText, setReasonText ] = useState('');
  const [ step, setStep ] = useState<StepType>('REASONS');
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() < 3;

  const reasonOptions = useMemo(() => {
    if (poi && poi.dealType) {
      return optionsByDealType[poi.dealType].map(item => ({
        label: t('manageBulletinPage.bulletin.updateStatusPopup.options', { reason: item }),
        value: item,
      }));
    }
    return [];
  }, [ t, poi ]);

  useEffect(() => {
    if (status && poi && poi.id) {
      onPopupOpen();
    }
  }, []);

  useEffect(() => {
    setStep('REASONS');
  }, [ poi && poi.id, status ]);

  const modalTitle = useMemo(() => {
    if (status === BulletinStatusType.Deleted) {
      return t('manageBulletinPage.bulletin.delete');
    }
    if (status === BulletinStatusType.Frozen) {
      return t('manageBulletinPage.bulletin.freeze');
    }
    if (status === BulletinStatusType.CancelSubscription) {
      return t('manageBulletinPage.bulletin.cancelSubscription');
    }
    return undefined;
  }, [ t, status ]);

  if (!status || !poi || !poi.id) return null;

  const isAgentListing = poi && poi.poc && poi.poc.type === PocType.Agent;
  const isPrivateResidentialBulletin = poi.type === 'bulletin' && !isAgentListing;
  const isMonthlySubscriptionFlow = Boolean(
    (status === BulletinStatusType.Deleted || status === BulletinStatusType.Frozen || status === BulletinStatusType.CancelSubscription)
    && isPrivateResidentialBulletin
    && promotionPlan
    && promotionPlan.promotionPlan.variant === PromotionPlanVariant.SUBSCRIPTION_MONTHLY
  );
  const isDeleteOfBulletinWithOneTimePaymentPlanFlow = Boolean(
    status === BulletinStatusType.Deleted
    && isPrivateResidentialBulletin
    && promotionPlan
    && (promotionPlan.promotionPlan.variant === PromotionPlanVariant.UNTIL_SOLD)
  );
  const hasPromotionPlanStep = isMonthlySubscriptionFlow || isDeleteOfBulletinWithOneTimePaymentPlanFlow;

  const onClose = () => setModalOpenStatus(null);

  const buildHideReason = () => (reason === ReasonType.Other ? (trim(reasonText) || 'other') : reason);

  const onSubmitDelete = () => {
    const hideReason = buildHideReason();
    editListingStatus({ status, id: poi.id, type: poi.type, hideReason, source });
    onClose();
  };

  const onContinueToPromotionPlanStep = () => {
    setStep('PROMOTION_PLAN');
  };

  const onConfirmCancelSubscriptionAndEditStatus = () => {
    if (!promotionPlan || !cancelSubscriptionThenEditListingStatus) return;
    const userPromotionPlanId = 'userPromotionPlanId' in promotionPlan
      ? promotionPlan.userPromotionPlanId
      : promotionPlan.id;
    if (!userPromotionPlanId) return;

    const hideReason = buildHideReason();
    cancelSubscriptionThenEditListingStatus({
      userPromotionPlanId,
      editListingStatus: {
        status,
        id: poi.id,
        type: 'bulletin',
        hideReason,
        source,
      },
    });
    onClose();
  };

  const onReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextReason = e.target.value;
    setReason(nextReason);
  };

  const onReasonTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextReasonText = e.currentTarget.value;
    if (nextReasonText && nextReasonText.length > REASON_MAX_LENGTH) return;
    setReasonText(nextReasonText);
  };

  const footerStyle = isMobile ? {
    paddingTop: `${defaultTheme.spacing(2.5)}`,
    paddingBottom: `${defaultTheme.spacing(2.5)}`,
  } : {
    padding: `0 ${defaultTheme.spacing(4.5)}`,
  };

  const renderFooter = () => {
    if (isMonthlySubscriptionFlow && step === 'PROMOTION_PLAN') {
      const onClick = () => {
        if (status === BulletinStatusType.CancelSubscription) {
          onCancelPromotionPlan({ reason: buildHideReason(), userPromotionPlanId: 'userPromotionPlanId' in promotionPlan ? promotionPlan.userPromotionPlanId : promotionPlan.id });
          onClose();
        }
        else {
          onConfirmCancelSubscriptionAndEditStatus();
        }
      };

      let buttonText = '';
      switch (status) {
        case BulletinStatusType.CancelSubscription:
          buttonText = t('cancelBulletinSubscriptionPromotionPlanModal.confirm');
          break;
        case BulletinStatusType.Frozen:
          buttonText = t('manageBulletinPage.bulletin.freezeAfterSubscriptionCancel.confirm');
          break;
        case BulletinStatusType.Deleted:
          buttonText = t('manageBulletinPage.bulletin.deleteAfterSubscriptionCancel.confirm');
          break;
      }

      return (
        <Styled.ConfirmButton onClick={onClick} fullWidth size="extraLarge">
          {buttonText}
        </Styled.ConfirmButton>
      );
    }

    if (status === BulletinStatusType.CancelSubscription) {
      return (
        <Button
          mode="primary"
          onClick={onContinueToPromotionPlanStep}
          disabled={reason === null}
          fullWidth
          size="extraLarge"
          data-auto="continue-button"
        >
          {t('manageBulletinPage.bulletin.cancelSubscriptionAfterSubscriptionCancel.confirm')}
        </Button>
      );
    }
    if (isDeleteOfBulletinWithOneTimePaymentPlanFlow && step === 'PROMOTION_PLAN') {
      return (
        <Styled.ConfirmButton onClick={onSubmitDelete} fullWidth size="extraLarge">
          {t('manageBulletinPage.bulletin.deleteAfterSubscriptionCancel.confirm')}
        </Styled.ConfirmButton>
      );
    }

    if (hasPromotionPlanStep && step === 'REASONS') {
      return (
        <Button
          mode="primary"
          onClick={onContinueToPromotionPlanStep}
          disabled={reason === null}
          fullWidth
          size="extraLarge"
          data-auto="continue-button"
        >
          {t('manageBulletinPage.bulletin.updateStatusPopup.continue')}
        </Button>
      );
    }

    return (
      <Styled.ConfirmButton
        onClick={onSubmitDelete}
        disabled={reason === null}
        fullWidth
        size="extraLarge"
        data-auto="submit-button"
      >
        {t('manageBulletinPage.bulletin.updateStatusPopup.submit', { status })}
      </Styled.ConfirmButton>
    );
  };

  const renderBody = () => {
    if (hasPromotionPlanStep && step === 'PROMOTION_PLAN') {
      return (
        <Styled.Root data-auto={`update-listing-status-popup-${status}-and-cancel-promotion-plan`}>
          <PromotionPlanInfoSection status={status} promotionPlan={promotionPlan} rootStyle={{ paddingBottom: 24 }} />
        </Styled.Root>
      );
    }

    return (
      <Styled.Root data-auto={`update-listing-status-popup-${status}`}>
        {isAgentListing ? (
          <>
            <Text weight="bold" data-auto="update-listing-status-popup-header">
              {t('manageBulletinPage.bulletin.updateStatusPopup.header.agent', { status })}
            </Text>
            <Styled.AgentDescription>
              {t('manageBulletinPage.bulletin.updateStatusPopup.description.agent', { status })}
            </Styled.AgentDescription>
          </>
        ) : (
          <>
            <Text weight="bold" data-auto="update-listing-status-popup-header">
              {t('manageBulletinPage.bulletin.updateStatusPopup.header', { status })}
            </Text>
            <Styled.OptionsWrapper>
              <RadioGroup
                options={reasonOptions}
                value={reason}
                name="reason"
                onChange={onReasonChange}
              />
              <Styled.ReasonTextWrapper>
                {reason === ReasonType.Other ? (
                  <Textarea
                    rows={1}
                    charactersCounterMax={REASON_MAX_LENGTH}
                    onChange={onReasonTextChange}
                    value={reasonText}
                    autoFocus
                  />
                ) : null}
              </Styled.ReasonTextWrapper>
            </Styled.OptionsWrapper>
          </>
        )}
      </Styled.Root>
    );
  };

  return (
    <Modal
      isOpen
      desktopModalPositionCenter
      onClose={onClose}
      title={modalTitle}
      footerStyle={footerStyle}
      buttons={renderFooter()}
    >
      {renderBody()}
    </Modal>
  );
};

const UpdateListingStatusPopupWithEvents  = withEvents<UpdateListingStatusPopupProps>((sendEvent, props) => ({
  onPopupOpen() {
    if (props.poi && props.poi.poc && props.poi.poc.type === PocType.Agent) return;

    const eventName = props.status === BulletinStatusType.Frozen ? 'bulletin_manage_freeze_form_open' : 'bulletin_manage_delete_form_open';
    const poiPayload = poiEventPayload({
      poi: props.poi,
      estimatedPrice: null,
      isLeadingAgent: false,
      currentUser: props.currentUser,
    });

    sendEvent(eventName, 'bulletin_manage', {
      ...poiPayload,
      event: { source: props.source },
    });
  },
}))(UpdateListingStatusPopup);

export default UpdateListingStatusPopupWithEvents;
